import { RootState } from '@/store';
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Images from './Images';
import { ProfileAvatar } from './ProfileAvatar';
import { useGetUserProfileQuery } from '@/store/api/userApi';
import { useIsAuthenticated } from '@/hooks';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetNotificationsCountQuery } from '@/store/api/notificationApi';
import { AnimatePresence } from 'motion/react';
import { motion, useAnimation } from 'framer-motion';
interface HeaderProps {
  setToggle: React.Dispatch<React.SetStateAction<boolean>>;
}
const Header: React.FC<HeaderProps> = ({ setToggle }) => {
  const isAuthenticated = useIsAuthenticated();
  const { data, isLoading } = useGetUserProfileQuery(isAuthenticated ? undefined : skipToken);
  const bannerImage = useSelector((state: RootState) => state.header.bannerImage);
  const bgColor = useSelector((state: RootState) => state.header.bgColor);
  const user = useSelector((s: RootState) => s.user.profile);
  const hasBannerBg = Boolean(bannerImage);
  const { data: countData } = useGetNotificationsCountQuery(undefined, {
    pollingInterval: 30000, // refresh every 30s
    skip: !isAuthenticated,
  });
  const unreadCount = countData?.count ?? 0;
  const prevCount = useRef(unreadCount);
  const bellControls = useAnimation();
  const notificationAudio = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    notificationAudio.current = new Audio('/images/notification.mp3');
  }, []);

  useEffect(() => {
    if (prevCount.current === unreadCount) return;

    if (unreadCount > prevCount.current) {
      notificationAudio.current?.play().catch(() => {});

      bellControls.start({
        rotate: [0, -25, 25, -18, 18, -10, 10, 0],
        transition: {
          duration: 0.6,
          ease: 'easeInOut',
        },
      });
    }

    prevCount.current = unreadCount;
  }, [unreadCount, bellControls]);

  // useGetUserQuery(undefined, { pollingInterval: 5000 });
  return (
    <header className="fixed top-0 left-1/2 -translate-x-1/2 w-full max-w-[480px] text-white flex justify-between items-center px-3 py-2 z-20 bg-black">
      {/* ── Layer 1: Blurred banner image background ── */}
      {hasBannerBg && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
          <Images
            src={bannerImage}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              filter: 'blur(24px) saturate(1.8) brightness(0.45)',
              transform: 'scale(1.2)',
              transition: 'opacity 600ms ease',
            }}
          />
        </div>
      )}

      {/* ── Layer 2: bgColor overlay (transparent → solid on scroll) ── */}
      <div
        className="absolute inset-0 pointer-events-none transition-colors duration-500"
        style={{
          backgroundColor: hasBannerBg ? bgColor : bgColor,
          backdropFilter: hasBannerBg ? 'none' : 'blur(12px)',
          WebkitBackdropFilter: hasBannerBg ? 'none' : 'blur(12px)',
          zIndex: 1,
        }}
      />

      {/* ── Layer 3: Bottom subtle separator ── */}
      <div
        className="absolute bottom-0 left-0 right-0 h-px pointer-events-none"
        style={{
          background: hasBannerBg
            ? 'linear-gradient(to right, transparent, rgba(255,255,255,0.08), transparent)'
            : 'transparent',
          zIndex: 2,
        }}
      />

      {/* ── Layer 4: Actual nav content ── */}
      <div className="relative flex w-full justify-between items-center" style={{ zIndex: 3 }}>
        {/* Left — toggle + logo */}
        <div className="flex items-center justify-center gap-x-4">
          <button
            onClick={() => setToggle((prev) => !prev)}
            className="w-8 h-8 flex justify-center rounded-md hover:bg-white/10 transition"
          >
            <Images src="/home/toggle.svg" alt="toggle" className="w-6" />
          </button>
          <Link to="/">
            <Images src="/logo.svg" alt="logo" className="h-auto max-w-[140px]" />
          </Link>
        </div>

        {/* Right — notification + profile */}
        <div className="flex items-center gap-1 sm:gap-2 text-base">
          <Link
            to="/notification"
            className="relative rounded-full bg-btn-gradient2 w-8 h-8 flex justify-center items-center border"
          >
            <motion.div animate={bellControls} style={{ originY: '0px' }}>
              <Images src="/home/ball.svg" alt="notification" className="w-4" />
            </motion.div>

            <AnimatePresence>
              {unreadCount > 0 && (
                <motion.span
                  key={unreadCount}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                  className="absolute -top-1 -right-1 min-w-[16px] h-4 px-0.5 rounded-full
                bg-red-500 text-white text-[9px] font-bold flex items-center justify-center"
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </motion.span>
              )}
            </AnimatePresence>
          </Link>
          {isAuthenticated && (
            <Link
              to="/profile"
              className="rounded-full overflow-hidden border w-8 h-8 flex justify-center"
            >
              <ProfileAvatar
                size="sm"
                image={user?.profileImage || ''}
                fallbackSrc="/matches/nodata/fallbackprofile.webp"
              />
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
